import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  GradientButton,
  NeutralButton,
  SolidBlueButton,
} from "./trainingUi";
import { getTrainingQuizApi, submitTrainingQuizApi } from "../../api/training.api";

const TEXT = "#18212F";
const BORDER = "#E1E1E1";

const Quiz = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const toast = useToast();

  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!lessonId) return;
    setLoading(true);
    setAnswers({});
    setCurrentIndex(0);
    getTrainingQuizApi(lessonId)
      .then((data) => setQuiz(data || null))
      .catch(() => setQuiz(null))
      .finally(() => setLoading(false));
  }, [lessonId]);

  const questions = useMemo(() => quiz?.questions ?? [], [quiz]);
  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;

  const handleSelect = useCallback((questionId, optionKey) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }));
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const data = await submitTrainingQuizApi(lessonId, answers);
      setResult(data);
    } catch (e) {
      toast({
        title: e?.message || "Nộp bài thất bại",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setSubmitting(false);
    }
  }, [answers, lessonId, toast]);

  const closeResult = useCallback(() => {
    setResult(null);
    navigate(-1);
  }, [navigate]);

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="40px">
          <Spinner color={APP.primary200} />
        </Flex>
      ) : (
        <Box px="16px" pt="16px">
          {/* Banner tiêu đề bài học */}
          {quiz?.title && (
            <Box bg="#F1F5FB" borderRadius="12px" p="14px" mb="16px">
              <Text fontSize="14px" fontWeight={600} color={TEXT} lineHeight="24px">
                {quiz.title}
              </Text>
            </Box>
          )}

          {/* Lưới số câu hỏi */}
          <Box border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px" mb="16px">
            <Text fontSize="14px" fontWeight={600} color={TEXT} textAlign="center" mb="12px">
              Câu hỏi ({questions.length})
            </Text>
            {questions.length > 0 && (
              <Flex wrap="wrap" gap="10px" justify="center">
                {questions.map((q, idx) => {
                  const isCurrent = idx === currentIndex;
                  const isAnswered = !!answers[q._id];
                  return (
                    <Flex
                      key={q._id}
                      as="button"
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      w="52px"
                      h="52px"
                      borderRadius="8px"
                      align="center"
                      justify="center"
                      borderWidth={isCurrent ? "2px" : "1px"}
                      borderStyle="solid"
                      borderColor={
                        isCurrent || isAnswered ? APP.tabActive : BORDER
                      }
                      bg={isAnswered ? "#EFF6FF" : "white"}
                      color={isAnswered ? APP.tabActive : TEXT}
                      fontWeight={600}
                      fontSize="14px"
                    >
                      {q.order}
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </Box>

          {/* Câu hỏi hiện tại */}
          {currentQuestion && (
            <Box border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
              <Text fontSize="14px" fontWeight={600} color={TEXT} lineHeight="22px" mb="14px">
                Câu {currentQuestion.order}: {currentQuestion.content}
              </Text>

              {currentQuestion.options.map((opt) => {
                const selected = answers[currentQuestion._id] === opt.key;
                return (
                  <Flex
                    key={opt.key}
                    as="button"
                    type="button"
                    onClick={() => handleSelect(currentQuestion._id, opt.key)}
                    align="flex-start"
                    gap="10px"
                    py="10px"
                    w="100%"
                    textAlign="left"
                  >
                    <Flex
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      borderWidth="2px"
                      borderStyle="solid"
                      borderColor={selected ? APP.tabActive : "#CBD5E1"}
                      align="center"
                      justify="center"
                      mt="1px"
                      flexShrink={0}
                    >
                      {selected && (
                        <Box w="10px" h="10px" borderRadius="full" bg={APP.tabActive} />
                      )}
                    </Flex>
                    <Text fontSize="14px" color={TEXT} lineHeight="20px" flex="1">
                      {opt.key}. {opt.label}
                    </Text>
                  </Flex>
                );
              })}
            </Box>
          )}

          <Box h="20px" />

          {/* Nút hành động */}
          <SimpleGrid columns={2} spacing="12px">
            <GradientButton h="50px" isLoading={submitting} onClick={() => setConfirmOpen(true)}>
              Nộp bài ({answeredCount}/{questions.length})
            </GradientButton>
            <NeutralButton h="50px" onClick={() => navigate(-1)}>
              Quay lại
            </NeutralButton>
          </SimpleGrid>
        </Box>
      )}

      {/* Xác nhận nộp bài */}
      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.45)" />
        <ModalContent mx="24px" borderRadius="16px" maxW="420px">
          <ModalBody p="20px">
            <Text fontSize="16px" fontWeight={700} color={TEXT}>
              Xác nhận nộp bài
            </Text>
            <Box h="14px" />
            <Text fontSize="14px" color={TEXT} lineHeight="20px">
              Bạn đã trả lời{" "}
              <Text as="span" fontWeight={700}>
                {answeredCount} / {questions.length}
              </Text>{" "}
              câu.
            </Text>
            <Box h="8px" />
            <Text fontSize="14px" color="#64748B" lineHeight="20px">
              Câu chưa trả lời sẽ được tính là{" "}
              <Text as="span" fontWeight={700} color="#EF2E14">
                sai
              </Text>
              .
            </Text>
            <Box h="8px" />
            <Text fontSize="14px" color={TEXT} lineHeight="20px">
              Bạn có chắc muốn nộp bài không?
            </Text>
            <Box h="20px" />
            <SimpleGrid columns={2} spacing="12px">
              <NeutralButton h="48px" onClick={() => setConfirmOpen(false)}>
                Hủy
              </NeutralButton>
              <GradientButton h="48px" onClick={handleConfirmSubmit}>
                Nộp bài
              </GradientButton>
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Kết quả bài thi */}
      <Modal isOpen={!!result} onClose={closeResult} isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.45)" />
        <ModalContent mx="24px" borderRadius="16px" maxW="420px">
          <ModalBody p="20px">
            <Flex direction="column" align="center">
              <Text fontSize="16px" fontWeight={700} color={TEXT}>
                Kết quả bài thi
              </Text>
              <Box h="12px" />
              <Text fontSize="32px" fontWeight={800} color={APP.tabActive}>
                {result?.score ?? 0}/100
              </Text>
              <Box h="6px" />
              <Text fontSize="14px" color="#64748B">
                Số câu đúng: {result?.correctCount ?? 0}/
                {result?.totalQuestions ?? 0}
              </Text>
              <Box h="20px" />
              <SolidBlueButton h="48px" onClick={closeResult}>
                Đóng
              </SolidBlueButton>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Làm bài trắc nghiệm" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Làm bài trắc nghiệm" />
      {pageBody}
    </Box>
  );
};

export default Quiz;
